import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, X, Clock, CheckCircle2, Sparkles } from 'lucide-react';
import api from '../api/axios';
import Confetti from 'react-confetti';

const DailyCreditsModal = ({ isOpen, onClose, onCreditsClaimed }) => {
  const [canClaim, setCanClaim] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [remainingTime, setRemainingTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (isOpen) {
      checkClaimStatus();
    }
  }, [isOpen]);

  // Update countdown every second
  useEffect(() => {
    if (remainingTime && remainingTime.milliseconds > 0) {
      const timer = setInterval(() => {
        setRemainingTime(prev => {
          if (!prev || prev.milliseconds <= 1000) {
            clearInterval(timer);
            checkClaimStatus(); // Recheck when timer expires
            return null;
          }
          
          const newMilliseconds = prev.milliseconds - 1000;
          const hours = Math.floor(newMilliseconds / (60 * 60 * 1000));
          const minutes = Math.floor((newMilliseconds % (60 * 60 * 1000)) / (60 * 1000));
          const seconds = Math.floor((newMilliseconds % (60 * 1000)) / 1000);
          
          return {
            hours,
            minutes,
            seconds,
            milliseconds: newMilliseconds
          };
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [remainingTime]);

  const checkClaimStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/user/daily-credits-status');
      
      if (response.data.success) {
        setCanClaim(response.data.canClaim);
        if (!response.data.canClaim && response.data.remainingTime) {
          setRemainingTime(response.data.remainingTime);
        }
      }
    } catch (error) {
      console.error('Failed to check claim status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    try {
      setClaiming(true);
      const response = await api.post('/user/claim-daily-credits');
      
      if (response.data.success) {
        setClaimed(true);
        setShowConfetti(true);
        
        // Hide confetti after 5 seconds
        setTimeout(() => setShowConfetti(false), 5000);
        
        // Call parent callback to update credits
        if (onCreditsClaimed) {
          onCreditsClaimed(response.data.newBalance);
        }
        
        // Close modal after 3 seconds
        setTimeout(() => {
          onClose();
          setClaimed(false);
        }, 3000);
      } else {
        // Already claimed, update remaining time
        if (response.data.remainingTime) {
          setCanClaim(false);
          setRemainingTime(response.data.remainingTime);
        }
      }
    } catch (error) {
      console.error('Failed to claim credits:', error);
    } finally {
      setClaiming(false);
    }
  };

  const formatTime = (time) => {
    if (!time) return '00:00:00';
    return `${String(time.hours).padStart(2, '0')}:${String(time.minutes).padStart(2, '0')}:${String(time.seconds).padStart(2, '0')}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {showConfetti && (
            <Confetti
              width={window.innerWidth}
              height={window.innerHeight}
              recycle={false}
              numberOfPieces={500}
              gravity={0.3}
            />
          )}
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 rounded-2xl border border-purple-500/30 shadow-2xl max-w-md w-full overflow-hidden relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-lg transition-colors z-10"
              >
                <X size={20} className="text-gray-400" />
              </button>

              {/* Content */}
              <div className="p-8 text-center">
                {loading ? (
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
                    <p className="text-gray-400">Loading...</p>
                  </div>
                ) : claimed ? (
                  // Success State
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    className="space-y-4"
                  >
                    <div className="w-24 h-24 mx-auto bg-green-500/20 rounded-full flex items-center justify-center">
                      <CheckCircle2 size={48} className="text-green-400" />
                    </div>
                    <h2 className="text-3xl font-bold text-white">Success! 🎉</h2>
                    <p className="text-xl text-green-400">+20 Credits Added!</p>
                    <p className="text-gray-400 text-sm">Come back tomorrow for more free credits</p>
                  </motion.div>
                ) : canClaim ? (
                  // Claimable State
                  <div className="space-y-6">
                    <motion.div
                      animate={{ 
                        rotate: [0, -10, 10, -10, 0],
                        scale: [1, 1.1, 1.1, 1.1, 1]
                      }}
                      transition={{ 
                        duration: 0.5,
                        repeat: Infinity,
                        repeatDelay: 2
                      }}
                      className="w-24 h-24 mx-auto bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center relative"
                    >
                      <Gift size={48} className="text-white" />
                      <motion.div
                        animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="absolute inset-0 bg-purple-500 rounded-full"
                      />
                    </motion.div>

                    <div>
                      <h2 className="text-3xl font-bold text-white mb-2">Daily Free Credits! 🎁</h2>
                      <p className="text-gray-300">Claim your free 20 credits now!</p>
                    </div>

                    <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-6">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Sparkles size={20} className="text-yellow-400" />
                        <span className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                          20
                        </span>
                        <Sparkles size={20} className="text-yellow-400" />
                      </div>
                      <p className="text-sm text-gray-400">Credits will be added to your account</p>
                    </div>

                    <button
                      onClick={handleClaim}
                      disabled={claiming}
                      className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-xl font-bold text-white text-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {claiming ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Claiming...
                        </div>
                      ) : (
                        'Claim Now!'
                      )}
                    </button>

                    <p className="text-xs text-gray-500">* Available once every 24 hours</p>
                  </div>
                ) : (
                  // Already Claimed State with Timer
                  <div className="space-y-6">
                    <div className="w-24 h-24 mx-auto bg-gray-700/50 rounded-full flex items-center justify-center">
                      <Clock size={48} className="text-gray-400" />
                    </div>

                    <div>
                      <h2 className="text-2xl font-bold text-white mb-2">Already Claimed! 😊</h2>
                      <p className="text-gray-400">Come back later to claim your next reward</p>
                    </div>

                    <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
                      <p className="text-sm text-gray-400 mb-3">Next claim available in:</p>
                      <div className="text-4xl font-mono font-bold text-white">
                        {formatTime(remainingTime)}
                      </div>
                      <div className="flex justify-center gap-4 mt-3 text-xs text-gray-500">
                        <span>HOURS</span>
                        <span>MINS</span>
                        <span>SECS</span>
                      </div>
                    </div>

                    <button
                      onClick={onClose}
                      className="w-full py-3 bg-gray-700 hover:bg-gray-600 rounded-xl font-semibold text-white transition-all"
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DailyCreditsModal;
